'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
  GitBranch,
  RefreshCw,
  Phone,
  Power,
} from 'lucide-react'
import { useCallFlows, useCallFlowMutations } from '@/hooks/pbx'
import { usePbxPhoneNumbersQuery } from '@/hooks/queries/pbx-phone-numbers'
import type { CallFlow } from '@/lib/pbx/flow/types'
import { formatDistanceToNow } from 'date-fns'
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




interface CallFlowsTabProps {
  canManage: boolean
  onEditFlow: (flowId: string) => void
  onNewFlow: () => void
}

export function CallFlowsTab({ canManage, onEditFlow, onNewFlow }: CallFlowsTabProps) {

  const tUi = useTranslations("Pbx.PbxPagesCallFlowsTab")
  const tCommonUi = useTranslations("Common")

  const { flows, isLoading, error, refetch } = useCallFlows()
  const { deleteFlow, syncFlow, updateFlow, isLoading: isMutating } = useCallFlowMutations()
  const phoneNumbersQuery = usePbxPhoneNumbersQuery(Boolean(flows.length))

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [flowToDelete, setFlowToDelete] = useState<CallFlow | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteConfirmed, setDeleteConfirmed] = useState(false)
  const [syncingFlowId, setSyncingFlowId] = useState<string | null>(null)
  const [togglingFlowId, setTogglingFlowId] = useState<string | null>(null)
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false)
  const [flowToDeactivate, setFlowToDeactivate] = useState<CallFlow | null>(null)
  const phones = phoneNumbersQuery.data ?? []
  const flowPhones = phones.reduce<Record<string, string[]>>((phonesMap, phone) => {
    if (phone.call_flow_id && phone.is_enabled) {
      if (!phonesMap[phone.call_flow_id]) phonesMap[phone.call_flow_id] = []
      phonesMap[phone.call_flow_id].push(phone.phone_number)
    }
    return phonesMap
  }, {})
  const flowTwilioPhones = phones.reduce<Record<string, string[]>>((phonesMap, phone) => {
    if (phone.call_flow_id && phone.is_enabled && phone.provider !== 'sip_trunk') {
      if (!phonesMap[phone.call_flow_id]) phonesMap[phone.call_flow_id] = []
      phonesMap[phone.call_flow_id].push(phone.phone_number)
    }
    return phonesMap
  }, {})
  const flowExternalSipPhones = phones.reduce<Record<string, string[]>>((phonesMap, phone) => {
    if (phone.call_flow_id && phone.is_enabled && phone.provider === 'sip_trunk') {
      if (!phonesMap[phone.call_flow_id]) phonesMap[phone.call_flow_id] = []
      phonesMap[phone.call_flow_id].push(phone.phone_number)
    }
    return phonesMap
  }, {})

  const handleDeleteClick = (flow: CallFlow) => {
    setFlowToDelete(flow)
    setDeleteError(null)
    setDeleteConfirmed(false)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!flowToDelete) return

    const hasAssignedPhones = flowPhones[flowToDelete.id]?.length > 0

    setDeleteError(null)
    try {
      // Use force=true if flow has assigned phones and user confirmed
      await deleteFlow(flowToDelete.id, hasAssignedPhones && deleteConfirmed)
      setDeleteDialogOpen(false)
      setFlowToDelete(null)
      setDeleteConfirmed(false)
      // Refresh the list after deletion
      refetch()
      void phoneNumbersQuery.refetch()
    } catch (err) {
      // Show error in dialog
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete flow')
    }
  }

  const handleSync = async (flow: CallFlow) => {
    setSyncingFlowId(flow.id)
    try {
      const result = await syncFlow(flow.id)
      if (result.warning === 'NO_ASSIGNED_NUMBERS') {
        // Not an error - just no phones assigned
        // Could show info toast but it's clear from the UI
      }
    } catch {
      // Error is handled by the hook
    } finally {
      setSyncingFlowId(null)
    }
  }

  const handleToggleActive = (flow: CallFlow) => {
    // If deactivating and has assigned phones, show confirmation dialog
    if (flow.is_active && flowPhones[flow.id]?.length > 0) {
      setFlowToDeactivate(flow)
      setDeactivateDialogOpen(true)
      return
    }

    // Otherwise toggle directly
    performToggleActive(flow)
  }

  const performToggleActive = async (flow: CallFlow) => {
    setTogglingFlowId(flow.id)
    try {
      await updateFlow(flow.id, { is_active: !flow.is_active })
      refetch()
      void phoneNumbersQuery.refetch()
    } catch {
      // Error is handled by the hook
    } finally {
      setTogglingFlowId(null)
    }
  }

  const handleConfirmDeactivate = async () => {
    if (!flowToDeactivate) return

    setDeactivateDialogOpen(false)
    await performToggleActive(flowToDeactivate)
    setFlowToDeactivate(null)
  }

  const getSyncStatusBadge = (flow: CallFlow, hasTwilioPhones: boolean, hasExternalSipPhones: boolean) => {
    if (!hasTwilioPhones && hasExternalSipPhones) {
      return (
        <Badge variant="outline" className="text-indigo-600 border-indigo-300">
          <span key={tUi("externalSIP")}>{tUi("externalSIP")}</span></Badge>
      )
    }

    if (!flow.synced_at) {
      return (
        <Badge variant="outline" className="text-yellow-600 border-yellow-300">
          <Clock className="w-3 h-3 mr-1" />
          <span><span key={tUi("notSynced")}>{tUi("notSynced")}</span></span>
        </Badge>
      )
    }

    if (flow.sync_error) {
      return (
        <Badge variant="outline" className="text-red-600 border-red-300">
          <XCircle className="w-3 h-3 mr-1" />
          <span><span key={tUi("syncError")}>{tUi("syncError")}</span></span>
        </Badge>
      )
    }

    return (
      <Badge variant="outline" className="text-green-600 border-green-300">
        <CheckCircle className="w-3 h-3 mr-1" />
        <span><span key={tUi("synced")}>{tUi("synced")}</span></span>
      </Badge>
    )
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center text-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        <p className="text-muted-foreground mt-2"><span key={tUi("loadingFlows")}>{tUi("loadingFlows")}</span></p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert className="border-red-200 bg-red-50">
        <AlertCircle className="h-4 w-4 text-red-600" />
        <AlertDescription className="text-red-800">{error}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-4 pb-28 sm:pb-8">
      {/* Flow List */}
      {flows.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <GitBranch className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                <span key={tUi("noInboundCallFlows")}>{tUi("noInboundCallFlows")}</span></h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-4">
                <span key={tUi("createYourFirstInboundCallFlowToStart")}>{tUi("createYourFirstInboundCallFlowToStart")}</span></p>
              {canManage && (
                <Button onClick={onNewFlow}>
                  <Plus className="w-4 h-4 mr-2" />
                  <span><span key={tUi("createInboundFlow")}>{tUi("createInboundFlow")}</span></span>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {flows.map((flow: CallFlow) => {
            const assignedPhones = flowPhones[flow.id] ?? []
            const assignedTwilioPhones = flowTwilioPhones[flow.id] ?? []
            const assignedExternalSipPhones = flowExternalSipPhones[flow.id] ?? []
            const hasTwilioPhones = assignedTwilioPhones.length > 0
            const hasExternalSipPhones = assignedExternalSipPhones.length > 0
            const shouldShowTwilioSync = hasTwilioPhones || !hasExternalSipPhones

            return (
            <Card key={flow.id} className="transition-shadow hover:shadow-md">
              <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <CardTitle className="flex flex-wrap items-center gap-2 break-words text-lg font-medium">
                      {flow.name}
                      {flow.is_active ? (
                        <Badge className="bg-green-100 text-green-800">
                          <span key={tUi("active")}>{tUi("active")}</span></Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500 border-gray-300">
                          <span key={tUi("inactive")}>{tUi("inactive")}</span></Badge>
                      )}
                    </CardTitle>
                    {flow.description && (
                      <p className="text-sm text-muted-foreground">
                        {flow.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {getSyncStatusBadge(flow, hasTwilioPhones, hasExternalSipPhones)}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                {/* Assigned Phones */}
                {assignedPhones.length > 0 && (
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    {assignedPhones.map((phone) => (
                      <Badge key={phone} variant="secondary" className="text-xs">
                        {phone}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-muted-foreground">
                    <span><TranslationText>{tUi.rich("value0NodesValue1Connections", { value0: String(flow.nodes.length), value1: String(flow.edges.length) })}</TranslationText></span>
                    {shouldShowTwilioSync && flow.synced_at && (
                      <span className="ml-4"><TranslationText>{tUi.rich("syncedValue0", { value0: String(formatDistanceToNow(new Date(flow.synced_at), { addSuffix: true })) })}</TranslationText></span>
                    )}
                    {!hasTwilioPhones && hasExternalSipPhones && (
                      <span className="ml-4">
                        <span key={tUi("twilioSyncNotRequiredForExternalSIP")}>{tUi("twilioSyncNotRequiredForExternalSIP")}</span></span>
                    )}
                  </div>

                  {canManage && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleActive(flow)}
                        disabled={togglingFlowId === flow.id}
                        title={flow.is_active ? tUi("deactivateFlow") : tUi("activateFlow")}
                        className={flow.is_active ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50' : 'text-green-600 hover:text-green-700 hover:bg-green-50'}
                      >
                        {togglingFlowId === flow.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Power className="w-4 h-4" />
                        )}
                        <span key={flow.is_active ? tUi("deactivate") : tUi("activate")} className="ml-2">{flow.is_active ? tUi("deactivate") : tUi("activate")}</span>
                      </Button>
                      {shouldShowTwilioSync && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSync(flow)}
                          disabled={syncingFlowId === flow.id || !flow.is_active}
                          title={!flow.is_active ? tUi("activateFlowBeforeSyncing") : tUi("syncToTwilio")}
                        >
                          {syncingFlowId === flow.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                          <span className="ml-2"><span key={tUi("sync")}>{tUi("sync")}</span></span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditFlow(flow.id)}
                      >
                        <Edit2 className="w-4 h-4" />
                        <span className="ml-2"><span key={tUi("edit")}>{tUi("edit")}</span></span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClick(flow)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {shouldShowTwilioSync && flow.sync_error && (
                  <Alert className="mt-3 border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800 text-sm">
                      {flow.sync_error}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
            )
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open)
        if (!open) {
          setDeleteError(null)
          setDeleteConfirmed(false)
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><span key={tUi("deleteInboundCallFlow")}>{tUi("deleteInboundCallFlow")}</span></DialogTitle>
            <DialogDescription>
              <span><span key={tUi("areYouSureYouWantToDelete")}>{tUi("areYouSureYouWantToDelete")}</span></span>{flowToDelete?.name}<span><span key={tUi("thisActionCannotBeUndone")}>{tUi("thisActionCannotBeUndone")}</span></span>
            </DialogDescription>
          </DialogHeader>

          {flowToDelete && flowPhones[flowToDelete.id]?.length > 0 && (
            <>
              <Alert className="border-orange-200 bg-orange-50">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800">
                  <p className="font-medium mb-2">
                    <span><span key={tUi("thisFlowHas")}>{tUi("thisFlowHas")}</span></span> {flowPhones[flowToDelete.id].length} <span><span key={tUi("assignedPhoneNumberS")}>{tUi("assignedPhoneNumberS")}</span></span>
                  </p>
                  {flowTwilioPhones[flowToDelete.id]?.length > 0 && (
                    <div className="mb-3">
                      <p className="font-medium"><span key={tUi("twilioPBXNumbers")}>{tUi("twilioPBXNumbers")}</span></p>
                      <ul className="list-disc list-inside">
                        {flowTwilioPhones[flowToDelete.id].map((phone, index) => (
                          <li key={`${phone}-${index}`}>{phone}</li>
                        ))}
                      </ul>
                      <p className="mt-1">
                        <span key={tUi("theseNumbersWillBeDisconnectedFromPBXAnd")}>{tUi("theseNumbersWillBeDisconnectedFromPBXAnd")}</span></p>
                    </div>
                  )}
                  {flowExternalSipPhones[flowToDelete.id]?.length > 0 && (
                    <div>
                      <p className="font-medium"><span key={tUi("externalSIPNumbers")}>{tUi("externalSIPNumbers")}</span></p>
                      <ul className="list-disc list-inside">
                        {flowExternalSipPhones[flowToDelete.id].map((phone, index) => (
                          <li key={`${phone}-${index}`}>{phone}</li>
                        ))}
                      </ul>
                      <p className="mt-1">
                        <span key={tUi("theseNumbersWillBeUnassignedFromTheElevenLabs")}>{tUi("theseNumbersWillBeUnassignedFromTheElevenLabs")}</span></p>
                    </div>
                  )}
                </AlertDescription>
              </Alert>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="delete-confirm"
                  checked={deleteConfirmed}
                  onCheckedChange={(checked) => setDeleteConfirmed(checked === true)}
                />
                <Label htmlFor="delete-confirm" className="text-sm cursor-pointer">
                  <span key={tUi("iUnderstandAndWantToDeleteThisFlow")}>{tUi("iUnderstandAndWantToDeleteThisFlow")}</span></Label>
              </div>
            </>
          )}

          {deleteError && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {deleteError}
              </AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isMutating || Boolean(flowToDelete && flowPhones[flowToDelete.id]?.length > 0 && !deleteConfirmed)}
            >
              {isMutating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              <span><span key={tUi("delete")}>{tUi("delete")}</span></span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><span key={tUi("deactivateInboundCallFlow")}>{tUi("deactivateInboundCallFlow")}</span></DialogTitle>
            <DialogDescription>
              <span><span key={tUi("areYouSureYouWantToDeactivate")}>{tUi("areYouSureYouWantToDeactivate")}</span></span>{flowToDeactivate?.name}<span>&quot;?</span>
            </DialogDescription>
          </DialogHeader>

          <Alert className="border-orange-200 bg-orange-50">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <p className="font-medium mb-2">
                <span><span key={tUi("thisFlowHas")}>{tUi("thisFlowHas")}</span></span> {flowToDeactivate && flowPhones[flowToDeactivate.id]?.length || 0} <span><span key={tUi("assignedPhoneNumberS")}>{tUi("assignedPhoneNumberS")}</span></span>
              </p>
              {flowToDeactivate && flowTwilioPhones[flowToDeactivate.id]?.length > 0 && (
                <div className="mb-3">
                  <p className="font-medium"><span key={tUi("twilioPBXNumbers")}>{tUi("twilioPBXNumbers")}</span></p>
                  <ul className="list-disc list-inside">
                    {flowTwilioPhones[flowToDeactivate.id].map((phone, index) => (
                      <li key={`${phone}-${index}`}>{phone}</li>
                    ))}
                  </ul>
                  <p className="mt-1">
                    <span key={tUi("theseNumbersWillBeDisconnectedFromPBXAnd")}>{tUi("theseNumbersWillBeDisconnectedFromPBXAnd")}</span></p>
                </div>
              )}
              {flowToDeactivate && flowExternalSipPhones[flowToDeactivate.id]?.length > 0 && (
                <div>
                  <p className="font-medium"><span key={tUi("externalSIPNumbers")}>{tUi("externalSIPNumbers")}</span></p>
                  <ul className="list-disc list-inside">
                    {flowExternalSipPhones[flowToDeactivate.id].map((phone, index) => (
                      <li key={`${phone}-${index}`}>{phone}</li>
                    ))}
                  </ul>
                  <p className="mt-1">
                    <span key={tUi("theseNumbersWillBeUnassignedFromTheElevenLabs")}>{tUi("theseNumbersWillBeUnassignedFromTheElevenLabs")}</span></p>
                </div>
              )}
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeactivateDialogOpen(false)
                setFlowToDeactivate(null)
              }}
            >
              <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeactivate}
              disabled={isMutating}
            >
              {isMutating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              <span><span key={tUi("deactivate")}>{tUi("deactivate")}</span></span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
